import { Android, Apple, PhoneAndroid, Schedule } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
  clearEstimatedRecipients,
  clearNotificationState,
  estimateRecipients,
  getBroadcastQuota,
  sendNotification,
} from "../../actions/notifications";
import { isSuperAdmin } from "../../utils/utils";
import type { FormData } from "./NotificationsForm";
import type { TargetData } from "./NotificationsTarget";

interface Props {
  formData: FormData;
  targetData: TargetData;
}

export const NotificationsSend = ({ formData, targetData }: Props) => {
  const dispatch = useDispatch();
  const {
    sending,
    sendSuccess,
    sendError,
    quota,
    quotaLoading,
    estimatedRecipients,
    estimatedRecipientsLoading,
    // biome-ignore lint/suspicious/noExplicitAny: V1 legacy code without typed Redux store
  } = useSelector((state: any) => state.notifications);
  // biome-ignore lint/suspicious/noExplicitAny: V1 legacy code without typed Redux store
  const adminDetails = useSelector((state: any) => state.auth.adminDetails);
  // biome-ignore lint/suspicious/noExplicitAny: V1 legacy code without typed Redux store
  const whitelabels = useSelector((state: any) => state.cities.whiteLabelList);
  const lastHandledRef = useRef<string | null>(null);

  const isSuper = isSuperAdmin(adminDetails);
  const currentWhitelabel = React.useMemo(() => {
    if (isSuper) return null;
    if (adminDetails?.whiteLabelId) {
      const wl = whitelabels?.find(
        // biome-ignore lint/suspicious/noExplicitAny: V1 legacy code without typed Redux store
        (w: any) => w.id === adminDetails.whiteLabelId
      );
      if (wl) return wl;
      return {
        id: adminDetails.whiteLabelId,
        name: adminDetails.whiteLabelCode || "Your App",
      };
    }
    return null;
  }, [isSuper, adminDetails, whitelabels]);

  useEffect(() => {
    dispatch(getBroadcastQuota());
  }, [dispatch]);

  useEffect(() => {
    const platform =
      formData.platform !== "all" ? formData.platform : undefined;
    if (
      targetData.strategy === "cities" &&
      targetData.selectedCities.length > 0
    ) {
      dispatch(
        estimateRecipients({
          city_id: targetData.selectedCities.map((c) => c.id),
          platform,
        })
      );
    } else if (targetData.strategy === "everyone" && currentWhitelabel) {
      dispatch(
        estimateRecipients({ whitelabel_id: currentWhitelabel.id, platform })
      );
    } else if (targetData.strategy === "apps" && targetData.selectedApp) {
      dispatch(
        estimateRecipients({
          whitelabel_id: targetData.selectedApp.id,
          platform,
        })
      );
    } else {
      dispatch(clearEstimatedRecipients());
    }
  }, [
    dispatch,
    targetData.strategy,
    targetData.selectedCities,
    targetData.selectedApp,
    currentWhitelabel,
    formData.platform,
  ]);

  useEffect(() => {
    if (sendSuccess) {
      dispatch(getBroadcastQuota());
    }
  }, [sendSuccess, dispatch]);

  useEffect(() => {
    if (
      sendSuccess &&
      lastHandledRef.current !== `success-${sendSuccess.notification_id}`
    ) {
      lastHandledRef.current = `success-${sendSuccess.notification_id}`;
      if (sendSuccess.status === "failed" && sendSuccess.error_message) {
        Swal.fire({
          title: "Failed to Send",
          text: sendSuccess.error_message,
          icon: "error",
          confirmButtonText: "OK",
          confirmButtonColor: "#f44336",
        }).then(() => {
          dispatch(clearNotificationState());
        });
      } else if (sendSuccess.status === "scheduled") {
        Swal.fire({
          title: "Notification Scheduled!",
          html: `
            <p>Your notification has been scheduled successfully.</p>
            <p style="color: #666; font-size: 14px; margin-top: 10px;">
              Notification ID: <strong>${sendSuccess.notification_id}</strong>
            </p>
          `,
          icon: "success",
          confirmButtonText: "OK",
          confirmButtonColor: "#4caf50",
        }).then(() => {
          dispatch(clearNotificationState());
        });
      } else {
        Swal.fire({
          title: "Notification Sent!",
          html: `
            <p>Your notification has been sent successfully.</p>
            <p style="color: #666; font-size: 14px; margin-top: 10px;">
              Notification ID: <strong>${sendSuccess.notification_id}</strong>
            </p>
          `,
          icon: "success",
          confirmButtonText: "OK",
          confirmButtonColor: "#4caf50",
        }).then(() => {
          dispatch(clearNotificationState());
        });
      }
    }
  }, [sendSuccess, dispatch]);

  useEffect(() => {
    if (sendError && lastHandledRef.current !== `error-${sendError}`) {
      lastHandledRef.current = `error-${sendError}`;
      Swal.fire({
        title: "Failed to Send",
        text: sendError,
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#f44336",
      }).then(() => {
        dispatch(clearNotificationState());
      });
    }
  }, [sendError, dispatch]);

  const getTargetSummary = () => {
    switch (targetData.strategy) {
      case "everyone":
        return currentWhitelabel
          ? `All users of ${currentWhitelabel.name}`
          : "All users";
      case "cities":
        if (targetData.selectedCities.length > 0) {
          const cityNames = targetData.selectedCities
            .map((c) => c.label)
            .join(", ");
          return `City: ${cityNames}`;
        }
        return "No city selected";
      case "users":
        if (targetData.selectedUsers?.length > 0) {
          return `${targetData.selectedUsers.length} user(s)`;
        }
        return "No users selected";
      case "apps":
        if (targetData.selectedApp) {
          return `App: ${targetData.selectedApp.label}`;
        }
        return "No app selected";
      default:
        return "No target selected";
    }
  };

  const getPlatformLabel = () => {
    switch (formData.platform) {
      case "ios":
        return "iOS only";
      case "android":
        return "Android only";
      default:
        return "All platforms";
    }
  };

  const getPlatformIcon = () => {
    switch (formData.platform) {
      case "ios":
        return <Apple fontSize="small" />;
      case "android":
        return <Android fontSize="small" />;
      default:
        return <PhoneAndroid fontSize="small" />;
    }
  };

  const isTargetingUsers = targetData.strategy === "users";
  const isBroadcast =
    targetData.strategy === "everyone" ||
    targetData.strategy === "cities" ||
    targetData.strategy === "apps";
  const quotaExhausted =
    isBroadcast &&
    quota &&
    (quota.daily_remaining <= 0 || quota.weekly_remaining <= 0);

  const hasValidTarget = () => {
    switch (targetData.strategy) {
      case "everyone":
        return !!currentWhitelabel;
      case "cities":
        return targetData.selectedCities.length > 0;
      case "users":
        return targetData.selectedUsers?.length > 0;
      case "apps":
        return !!targetData.selectedApp;
      default:
        return false;
    }
  };

  const canSend =
    formData.title &&
    formData.body &&
    hasValidTarget() &&
    !quotaExhausted &&
    (!formData.scheduleEnabled || formData.scheduledFor);

  const handleSend = async () => {
    const isScheduled = formData.scheduleEnabled && formData.scheduledFor;
    const scheduledTime = isScheduled
      ? formData.scheduledFor?.format("MMM D, YYYY [at] h:mm A")
      : null;

    const result = await Swal.fire({
      title: isScheduled ? "Schedule Notification?" : "Send Notification?",
      html: `
        <p><strong>Title:</strong> ${formData.title}</p>
        <p><strong>Target:</strong> ${getTargetSummary()}</p>
        <p><strong>Platform:</strong> ${getPlatformLabel()}</p>
        ${isScheduled ? `<p><strong>Scheduled for:</strong> ${scheduledTime}</p>` : ""}
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: isScheduled ? "Schedule" : "Send Now",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      let targetType = "topic";
      let targetValue: string | null = null;
      let targetUserIds: string[] | null = null;

      const buildTopicTarget = (baseTopic: string) => {
        if (formData.platform === "ios") {
          return {
            type: "condition",
            value: `'${baseTopic}' in topics && 'platform_os-ios' in topics`,
          };
        } else if (formData.platform === "android") {
          return {
            type: "condition",
            value: `'${baseTopic}' in topics && 'platform_os-android' in topics`,
          };
        }
        return { type: "topic", value: baseTopic };
      };

      switch (targetData.strategy) {
        case "everyone": {
          const topic = `whitelabel_id-${currentWhitelabel.id}`;
          const target = buildTopicTarget(topic);
          targetType = target.type;
          targetValue = target.value;
          break;
        }
        case "cities": {
          if (targetData.selectedCities.length > 0) {
            dispatch(
              sendNotification({
                title: formData.title,
                body: formData.body,
                type: formData.type,
                target_type: "cities",
                target_city_ids: targetData.selectedCities.map((c) => c.id),
                target_user_ids: null,
                target_platform:
                  formData.platform !== "all" ? formData.platform : null,
                scheduled_for: isScheduled
                  ? formData.scheduledFor?.toISOString()
                  : null,
              })
            );
            return;
          }
          break;
        }
        case "users": {
          targetType = "users";
          // biome-ignore lint/suspicious/noExplicitAny: V1 legacy code without typed user model
          targetUserIds = targetData.selectedUsers.map((u: any) =>
            String(u.id)
          );
          break;
        }
        case "apps": {
          const appTopic = `whitelabel_id-${targetData.selectedApp?.id}`;
          const target = buildTopicTarget(appTopic);
          targetType = target.type;
          targetValue = target.value;
          break;
        }
      }

      dispatch(
        sendNotification({
          title: formData.title,
          body: formData.body,
          type: formData.type,
          target_type: targetType,
          target_value: targetValue,
          target_user_ids: targetUserIds,
          target_platform:
            formData.platform !== "all" ? formData.platform : null,
          scheduled_for: isScheduled
            ? formData.scheduledFor?.toISOString()
            : null,
        })
      );
    }
  };

  const isScheduled = formData.scheduleEnabled && formData.scheduledFor;

  return (
    <Stack spacing={3}>
      <Typography variant="h5">Preview & Send</Typography>

      {quota && quota.daily_limit < 999 && isBroadcast && (
        <Paper
          sx={{
            p: 2,
            bgcolor: quotaExhausted ? "error.lighter" : "info.lighter",
          }}
        >
          <Typography
            variant="subtitle2"
            color="text.secondary"
            gutterBottom={true}
          >
            Broadcast Quota
          </Typography>
          <Box sx={{ display: "flex", gap: 3 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Daily
              </Typography>
              <Typography
                variant="h6"
                color={
                  quota.daily_remaining <= 0 ? "error.main" : "text.primary"
                }
              >
                {quota.daily_remaining} / {quota.daily_limit}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Weekly
              </Typography>
              <Typography
                variant="h6"
                color={
                  quota.weekly_remaining <= 0 ? "error.main" : "text.primary"
                }
              >
                {quota.weekly_remaining} / {quota.weekly_limit}
              </Typography>
            </Box>
          </Box>
          {quotaExhausted && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Broadcast quota exhausted. Individual user notifications are still
              available.
            </Alert>
          )}
        </Paper>
      )}

      {isTargetingUsers && (
        <Alert severity="info" icon={false}>
          <strong>No quota limits</strong> — Notifications to individual users
          don't count against your broadcast quota.
        </Alert>
      )}

      <Paper sx={{ p: 2, bgcolor: "grey.100" }}>
        <Typography
          variant="subtitle2"
          color="text.secondary"
          gutterBottom={true}
        >
          Preview
        </Typography>
        <Typography variant="h6">{formData.title || "(No title)"}</Typography>
        <Typography variant="body1" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
          {formData.body || "(No message)"}
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          sx={{ mt: 2 }}
          flexWrap="wrap"
          useFlexGap={true}
        >
          <Chip label={formData.type} size="small" color="primary" />
          <Chip label={getTargetSummary()} size="small" variant="outlined" />
          <Chip
            icon={getPlatformIcon()}
            label={getPlatformLabel()}
            size="small"
            variant="outlined"
          />
          {isScheduled && (
            <Chip
              icon={<Schedule fontSize="small" />}
              label={formData.scheduledFor?.format("MMM D, h:mm A")}
              size="small"
              color="secondary"
            />
          )}
        </Stack>
        {isBroadcast && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Estimated recipients:{" "}
              {estimatedRecipientsLoading ? (
                <CircularProgress size={14} sx={{ ml: 1 }} />
              ) : estimatedRecipients ? (
                <strong>
                  {estimatedRecipients.estimated_count.toLocaleString()}
                </strong>
              ) : (
                "—"
              )}
              {estimatedRecipients?.city_name && (
                <span style={{ marginLeft: 8, color: "#666" }}>
                  ({estimatedRecipients.city_name})
                </span>
              )}
            </Typography>
          </Box>
        )}
      </Paper>

      <Stack direction="row" spacing={2}>
        <Button
          variant="contained"
          size="large"
          onClick={handleSend}
          disabled={!canSend || sending}
          startIcon={isScheduled ? <Schedule /> : undefined}
        >
          {sending ? (
            <CircularProgress size={24} />
          ) : isScheduled ? (
            "Schedule Notification"
          ) : (
            "Send Notification"
          )}
        </Button>
      </Stack>
    </Stack>
  );
};
